/** Removes literal "undefined" from API/JS string concat (e.g. text + undefined → "…Croreundefined"). */
export function stripUndefinedArtifacts(s) {
  if (typeof s !== "string") return String(s ?? "");
  return s
    .replace(/\bundefined\b/gi, "")
    .replace(/\Bundefined\b/gi, "");
}

export function removeMarkdownTable(text) {
  if (!text || typeof text !== "string" || text === "undefined") return "";
  const lines = text.split("\n");
  const out = [];
  let i = 0;
  while (i < lines.length) {
    const trimmed = lines[i].trimStart();
    const looksLikeTableRow =
      trimmed.startsWith("|") && trimmed.indexOf("|", 1) !== -1;
    if (looksLikeTableRow) {
      let j = i;
      let consecutive = 0;
      while (j < lines.length) {
        const t = lines[j].trimStart();
        if (t.startsWith("|") && t.indexOf("|", 1) !== -1) {
          consecutive++;
          j++;
        } else {
          break;
        }
      }
      if (consecutive >= 2) {
        i = j;
        continue;
      }
    }
    out.push(lines[i]);
    i++;
  }
  return stripUndefinedArtifacts(out.join("\n"));
}

export function safeText(text) {
  if (text === null || text === undefined) return "";
  const s = String(text);
  if (s === "undefined" || s === "null") return "";
  return stripUndefinedArtifacts(s);
}

export function isCurrencyColumn(columnName) {
  const lowerCol = columnName.toLowerCase();
  return (
    lowerCol.includes("value") ||
    lowerCol.includes("amount") ||
    lowerCol.includes("price") ||
    lowerCol.includes("cost") ||
    lowerCol.includes("revenue") ||
    lowerCol.includes("sales") ||
    (lowerCol.includes("net_") && lowerCol.includes("value"))
  );
}

export function escapeHtml(text) {
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

// Format SQL for display: separate query blocks, put clauses on new lines, basic alignment
export function formatSqlForDisplay(sql) {
  if (!sql) return "";
  let formatted = String(sql).replace(/\s+/g, " ").trim();

  // Separate multiple query blocks (e.g. {Query 1}, {Query 2}) onto their own lines
  formatted = formatted.replace(/(\{Query\s*\d+\})/gi, "\n\n$1");

  const clausePatterns = [
    { regex: /\s+(SELECT)\b/gi, replacement: "\n$1 " },
    { regex: /\s+(WITH)\b/gi, replacement: "\n$1 " },
    { regex: /\s+(FROM)\b/gi, replacement: "\n$1 " },
    { regex: /\s+(WHERE)\b/gi, replacement: "\n$1 " },
    { regex: /\s+(GROUP BY)\b/gi, replacement: "\n$1 " },
    { regex: /\s+(HAVING)\b/gi, replacement: "\n$1 " },
    { regex: /\s+(ORDER BY)\b/gi, replacement: "\n$1 " },
    { regex: /\s+(LIMIT)\b/gi, replacement: "\n$1 " },
    { regex: /\s+(INNER JOIN|LEFT JOIN|RIGHT JOIN|JOIN)\b/gi, replacement: "\n  $1 " },
    { regex: /\s+(AS)\s+/gi, replacement: "\n  $1 " },
  ];

  clausePatterns.forEach(({ regex, replacement }) => {
    formatted = formatted.replace(regex, replacement);
  });

  // Collapse multiple blank lines to at most two newlines, then trim each line's leading space
  formatted = formatted.replace(/\n{3,}/g, "\n\n");
  formatted = formatted
    .split("\n")
    .map((line) => line.trimStart())
    .join("\n")
    .trim();

  return formatted;
}

export function highlightSql(sql) {
  if (!sql) return "";

  const formatted = formatSqlForDisplay(sql);
  let escaped = escapeHtml(formatted);

  // First, highlight the accent keywords (e.g., LIMIT) to match the
  // orange color in the reference design.
  escaped = escaped.replace(/\b(LIMIT)\b/gi, (match) => `<span class="sql-keyword-accent">${match.toUpperCase()}</span>`);

  // Then highlight the primary SQL keywords.
  escaped = escaped.replace(
    /\b(SELECT|WITH|FROM|WHERE|GROUP BY|ORDER BY|COUNT|AS|AND|OR|INNER JOIN|LEFT JOIN|RIGHT JOIN|JOIN|ON|HAVING|DISTINCT)\b/gi,
    (match) => `<span class="sql-keyword">${match.toUpperCase()}</span>`
  );

  // Replace {Query 1}, {Query 2} etc. with bold heading (no curly braces)
  escaped = escaped.replace(/\{Query\s*(\d+)\}/gi, (_, num) => `<span class="sql-query-heading">Query ${num}</span>`);

  return escaped;
}
